/**
 * -----------------------------------------------------------------------------
 * Managed Accounts Listing Page (Manager Level)
 * -----------------------------------------------------------------------------
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";
import { useNavigate } from "react-router-dom";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../components/admin/formFields";
import HeaderView from "../../components/admin/headerView";
import AdminTable from "../../components/adminTable";
import ListingTableHeader from "../../components/listingTableHeader";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../components/modalEditListingColumns";
import Pagination, { TPaginationSelected } from "../../components/pagination";
import { TAdminTableRowData } from "../../components/types";
import { APP_CONFIG } from "../../constants/constants";
import { useLocalStorage } from "../../utils/localStorageItems";
import { useListEnterprisesQuery } from "../../api/network/sysadminApiServices";
import { routeUrls } from "../../navigation/routeUrls";

const all_columns = [
  "Account ID",
  "Account Description",
  "Contact Name",
  "Contact Number",
  "Timezone",
  "Status",
  "Action",
];

const ScreenManagedAccountAdminDetails = () => {
  const { t: tMain } = useTranslation();
  const navigate = useNavigate();

  const [queryParams, setQueryParams] = useState({
    page: 1,
    page_size: 10,
    search: "",
    child_accounts: true,
  });

  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem("columns_ManagedAccounts");
  const [columns, setColumns] = useState<TListingColumn[]>(
    all_columns.map((colItem) => ({
      name: colItem,
      show: !!savedColumns ? savedColumns.includes(colItem) : true,
    }))
  );

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    setQueryParams({ ...queryParams, page: 1, search: value });
  }, 500);

  const handleLoginAs = (accountId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const newUrl = routeUrls.dashboardChildren.managedAccountUsers.replace(
      ":accountId",
      accountId
    );
    navigate(newUrl);
  };

  // fetch the child accounts
  const { data: accountsData, isFetching } =
    useListEnterprisesQuery(queryParams);
  const results = accountsData?.results || [];
  const count = accountsData?.count || 0;

  const tableData: TAdminTableRowData[] = results.map((item: any) => ({
    navLink: "",
    cellData: [
      item?.accountid,
      item?.description || "-",
      item?.contactname || "-",
      item?.contactphone || "-",
      item?.timezone || "-",
      item?.isactive === 1 ? (
        <span className="text-field-success">
          {tMain("active") || "Active"}
        </span>
      ) : (
        <span className="text-field-error-dark">
          {tMain("inactive") || "Inactive"}
        </span>
      ),
      <AdminFormFieldSubmit
        label={tMain("login_as") || "Login As..."}
        variant="primary"
        customWrapperClass="w-24"
        onClick={(e) => handleLoginAs(item.accountid, e)}
        disabled={item?.isactive !== 1}
      />,
    ].filter((_, index) => columns[index].show),
    isActive: item?.isactive === 1,
  }));

  return (
    <>
      <HeaderView title={"Managed Accounts"} />{" "}
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingTableHeader
          heading={"Child Accounts Listing"}
          searchBoxPlaceholder={"Search by Account ID or Name"}
          searchBoxValue={queryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
        />

        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={"columns_ManagedAccounts"}
        />

        <div className="py-4 mt-4 relative">
          <EditListingColumnsButton />

          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={isFetching}
            listingQueryParams={queryParams}
          />

          <Pagination
            pageSize={queryParams.page_size}
            handlePageSizeChange={(e: TSelectboxOption | null) => {
              setQueryParams({
                ...queryParams,
                page: 1,
                page_size: parseInt(`${e?.value}`),
              });
            }}
            totalPages={Math.ceil(count / queryParams.page_size) || 1}
            forcePage={queryParams.page - 1}
            handlePageClick={(data: TPaginationSelected) => {
              setQueryParams({ ...queryParams, page: data?.selected + 1 });
            }}
            paginCurrentPage={queryParams.page}
            paginItemsOnCurrentPage={results.length}
            paginTotalItemsCount={count}
          />
        </div>
      </div>
    </>
  );
};

export default ScreenManagedAccountAdminDetails;
